import { useState, useRef } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { ChevronDown, ChevronRight, LayoutDashboard, LogIn, Menu, Moon, ShoppingBag, Sun, User, UserPlus, X } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { useTheme } from '@/providers/theme-provider'
import { useAuth } from '@/providers/auth-provider'
import { appRoutes } from '@/constants'
import { useCarBrands } from '@/modules/cars/hooks/use-cars'
import { useCart } from '@/modules/cart/hooks/use-cart'
import { CartDrawer } from '@/modules/cart/components/cart-drawer'

const TOP_NAV_LINKS = [
  { label: 'Home', to: appRoutes.home },
  { label: 'Cars', to: appRoutes.cars },
  { label: 'Parts', to: appRoutes.parts },
]

const FALLBACK_BRANDS_NAV = [
  {
    name: 'AUDI',
    slug: 'audi',
    models: [
      { id: 1, name: 'A6', slug: 'audi-a6', bodyType: 'Sedan' },
      { id: 2, name: 'RS7 Sportback', slug: 'audi-rs7', bodyType: 'Coupe' },
      { id: 3, name: 'Q7', slug: 'audi-q7', bodyType: 'SUV' },
    ],
  },
  {
    name: 'BMW',
    slug: 'bmw',
    models: [
      { id: 4, name: '3 Series', slug: 'bmw-3-series', bodyType: 'Sedan' },
      { id: 5, name: 'X5', slug: 'bmw-x5', bodyType: 'SUV' },
      { id: 6, name: 'M4 Competition', slug: 'bmw-m4-competition', bodyType: 'Coupe' },
    ],
  },
  {
    name: 'MERCEDES-BENZ',
    slug: 'mercedes-benz',
    models: [
      { id: 7, name: 'C-Class', slug: 'mercedes-benz-c-class', bodyType: 'Sedan' },
      { id: 8, name: 'G-Class (G63 AMG)', slug: 'mercedes-benz-g-class', bodyType: 'SUV' },
    ],
  },
  {
    name: 'PORSCHE',
    slug: 'porsche',
    models: [
      { id: 9, name: '911 Carrera / GT3 RS', slug: 'porsche-911', bodyType: 'Coupe' },
    ],
  },
  {
    name: 'LAND ROVER',
    slug: 'land-rover',
    models: [
      { id: 10, name: 'Defender 110', slug: 'land-rover-defender', bodyType: 'SUV' },
      { id: 11, name: 'Range Rover Velar', slug: 'land-rover-range-rover-velar', bodyType: 'SUV' },
    ],
  },
  {
    name: 'MAHINDRA',
    slug: 'mahindra',
    models: [
      { id: 12, name: 'Thar 4x4', slug: 'mahindra-thar', bodyType: 'SUV' },
      { id: 13, name: 'XUV700', slug: 'mahindra-xuv700', bodyType: 'SUV' },
      { id: 14, name: 'Scorpio-N', slug: 'mahindra-scorpio-n', bodyType: 'SUV' },
    ],
  },
  {
    name: 'TOYOTA',
    slug: 'toyota',
    models: [
      { id: 15, name: 'Fortuner Legender', slug: 'toyota-fortuner', bodyType: 'SUV' },
      { id: 16, name: 'Innova Hycross', slug: 'toyota-innova-hycross', bodyType: 'MUV' },
    ],
  },
  {
    name: 'HYUNDAI',
    slug: 'hyundai',
    models: [
      { id: 17, name: 'Creta', slug: 'hyundai-creta', bodyType: 'SUV' },
      { id: 18, name: 'Verna', slug: 'hyundai-verna', bodyType: 'Sedan' },
    ],
  },
  {
    name: 'VOLKSWAGEN',
    slug: 'volkswagen',
    models: [
      { id: 19, name: 'Virtus GT', slug: 'volkswagen-virtus', bodyType: 'Sedan' },
    ],
  },
  {
    name: 'SKODA',
    slug: 'skoda',
    models: [
      { id: 20, name: 'Slavia TSI', slug: 'skoda-slavia', bodyType: 'Sedan' },
    ],
  },
]

export function PublicNavbar() {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isCartOpen, setIsCartOpen] = useState(false)
  const [floatingMenu, setFloatingMenu] = useState<{
    slug: string
    name: string
    models: any[]
    left: number
    top: number
  } | null>(null)

  const closeTimerRef = useRef<any>(null)

  const { theme, toggleTheme } = useTheme()
  const { isAuthenticated, isLoading, redirectPath } = useAuth()
  const { data: dbBrands } = useCarBrands()
  const { data: cartData } = useCart()
  const cartItemCount = cartData?.items?.reduce((acc: number, item: any) => acc + item.quantity, 0) || 0
  const navigate = useNavigate()
  const location = useLocation()

  const handleDashboardClick = () => {
    setIsMobileMenuOpen(false)
    navigate(redirectPath)
  }

  const handleMouseEnterBrand = (e: React.MouseEvent<HTMLDivElement>, brand: any) => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
    const rect = e.currentTarget.getBoundingClientRect()
    setFloatingMenu({
      slug: brand.slug,
      name: brand.name,
      models: brand.models || [],
      left: Math.max(16, Math.min(rect.left, window.innerWidth - 240)),
      top: rect.bottom + 2,
    })
  }

  const handleMouseLeaveBrand = () => {
    closeTimerRef.current = setTimeout(() => {
      setFloatingMenu(null)
    }, 280)
  }

  const handleMouseEnterDropdown = () => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current)
      closeTimerRef.current = null
    }
  }

  const handleMouseLeaveDropdown = () => {
    closeTimerRef.current = setTimeout(() => {
      setFloatingMenu(null)
    }, 220)
  }

  const brandsList = (dbBrands && dbBrands.length > 0)
    ? dbBrands.map((b: any) => ({
        ...b,
        models: (b.models && b.models.length > 0)
          ? b.models
          : (FALLBACK_BRANDS_NAV.find((fb) => fb.slug === b.slug)?.models || [])
      }))
    : FALLBACK_BRANDS_NAV

  return (
    <header
      className="sticky top-0 z-50 transition-colors"
      style={{
        backgroundColor: 'var(--color-bg-surface)',
        borderBottom: '1px solid var(--color-border-subtle)',
      }}
    >
      {/* ROW 1: TOP MAIN NAVIGATION BAR */}
      <div className="mx-auto flex h-16 max-w-[1400px] items-center justify-between px-4 sm:px-6 lg:px-8">
        {/* Logo */}
        <Link to="/" className="group flex shrink-0 items-center gap-2 py-1">
          <img
            src="/images/logo.png"
            alt="Exotic Autocart"
            className="h-14 w-auto object-contain sm:h-16"
          />
        </Link>

        {/* Nav items */}
        <nav className="hidden items-center gap-8 lg:flex">
          {TOP_NAV_LINKS.map((link) => {
            const isActive = location.pathname === link.to && !location.hash
            return (
              <Link
                key={link.label}
                to={link.to}
                className={`relative py-1 text-sm font-bold tracking-tight transition-colors ${
                  isActive ? 'text-[#CF2F2F]' : 'text-foreground/90 hover:text-[#CF2F2F]'
                }`}
              >
                {link.label}
                {isActive && (
                  <span className="absolute -bottom-1 left-0 right-0 h-[3px] rounded-full bg-[#CF2F2F] shadow-[0_2px_8px_rgba(207,47,47,0.5)]" />
                )}
              </Link>
            )
          })}
        </nav>

        {/* Right Section: Theme + Auth controls + Cart */}
        <div className="hidden items-center gap-4 sm:flex">
          <button
            type="button"
            onClick={toggleTheme}
            className="text-foreground/80 transition-colors hover:text-[#CF2F2F]"
            title={`Switch to ${theme === 'dark' ? 'Light' : 'Dark'} Mode`}
          >
            {theme === 'dark' ? (
              <Sun className="h-4.5 w-4.5 text-amber-400" />
            ) : (
              <Moon className="h-4.5 w-4.5" />
            )}
          </button>

          {/* Cart Trigger Button */}
          <button
            type="button"
            onClick={() => setIsCartOpen(true)}
            className="relative p-2 text-foreground/80 transition-colors hover:text-[#CF2F2F]"
            title="Shopping Cart"
          >
            <ShoppingBag className="h-5 w-5" />
            {cartItemCount > 0 && (
              <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#CF2F2F] px-1 text-[10px] font-bold text-white shadow-xs">
                {cartItemCount}
              </span>
            )}
          </button>

          {isLoading ? (
            <span className="h-9 w-24 animate-pulse rounded-lg bg-border/40" />
          ) : isAuthenticated ? (
            <div className="flex items-center gap-2.5">
              <button
                type="button"
                onClick={handleDashboardClick}
                className="text-foreground/80 transition-colors hover:text-[#CF2F2F]"
                title="My Account"
              >
                <User className="h-4.5 w-4.5" />
              </button>
              <Button size="sm" onClick={handleDashboardClick}>
                <LayoutDashboard className="h-4 w-4" /> Dashboard
              </Button>
            </div>
          ) : (
            <div className="flex items-center gap-2.5">
              <Button size="sm" variant="outline" onClick={() => navigate(appRoutes.auth.login)}>
                <LogIn className="h-4 w-4" /> Sign In
              </Button>
              <Button size="sm" onClick={() => navigate(appRoutes.auth.register)}>
                <UserPlus className="h-4 w-4" /> Sign Up
              </Button>
            </div>
          )}
        </div>

        {/* Mobile Menu Toggle */}
        <div className="flex items-center gap-2 sm:hidden">
          <button
            type="button"
            onClick={() => setIsCartOpen(true)}
            className="relative p-2 text-foreground"
            aria-label="Shopping Cart"
          >
            <ShoppingBag className="h-5 w-5" />
            {cartItemCount > 0 && (
              <span className="absolute 0 top-0 right-0 flex h-4 min-w-4 items-center justify-center rounded-full bg-[#CF2F2F] px-1 text-[10px] font-bold text-white">
                {cartItemCount}
              </span>
            )}
          </button>
          <button
            onClick={toggleTheme}
            className="p-2 text-foreground"
            aria-label="Toggle theme"
          >
            {theme === 'dark' ? <Sun className="h-4 w-4 text-amber-400" /> : <Moon className="h-4 w-4" />}
          </button>
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 text-foreground"
            aria-label="Toggle navigation menu"
          >
            {isMobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {/* ROW 2: DYNAMIC BRAND & MODEL NAVIGATION BAR */}
      <div
        className="py-2.5 relative z-40"
        style={{
          backgroundColor: 'var(--color-bg-surface)',
          borderTop: '1px solid var(--color-border-subtle)',
        }}
      >
        <div className="mx-auto flex max-w-[1400px] items-center px-4 sm:px-6 lg:px-8">
          {/* Scrollable Brands List */}
          <div className="flex items-center gap-6 minimal-red-scrollbar py-1 flex-1 relative">
            {brandsList.map((brand: any) => {
              const isOpen = floatingMenu?.slug === brand.slug

              return (
                <div
                  key={brand.slug}
                  className="relative shrink-0"
                  onMouseEnter={(e) => handleMouseEnterBrand(e, brand)}
                  onMouseLeave={handleMouseLeaveBrand}
                >
                  <button
                    type="button"
                    onClick={() => {
                      if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
                      setFloatingMenu(null)
                      navigate(`/cars/brands/${brand.slug}`)
                    }}
                    className={`flex items-center gap-1 text-[11px] font-extrabold uppercase tracking-wider transition-colors py-1.5 ${
                      isOpen ? 'text-[#CF2F2F]' : 'text-foreground/80 hover:text-[#CF2F2F]'
                    }`}
                  >
                    <span>{brand.name}</span>
                    <ChevronDown className={`h-3.5 w-3.5 text-muted-foreground transition-transform duration-200 ${isOpen ? 'rotate-180 text-[#CF2F2F]' : ''}`} />
                  </button>
                </div>
              )
            })}
          </div>
        </div>
      </div>

      {/* Floating Models Dropdown Menu (Fixed Positioning with Grace Period & Invisible Hover Bridge) */}
      <AnimatePresence>
        {floatingMenu && (
          <motion.div
            initial={{ opacity: 0, y: 6, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 4, scale: 0.96 }}
            transition={{ duration: 0.15 }}
            style={{
              position: 'fixed',
              left: `${floatingMenu.left}px`,
              top: `${floatingMenu.top}px`,
              zIndex: 99999,
            }}
            onMouseEnter={handleMouseEnterDropdown}
            onMouseLeave={handleMouseLeaveDropdown}
            className="before:absolute before:-top-4 before:left-0 before:right-0 before:h-4 min-w-[220px] rounded-xl border border-slate-200 dark:border-border bg-white dark:bg-card p-3 shadow-2xl backdrop-blur-md"
          >
            <div className="text-[10px] font-extrabold uppercase tracking-wider text-muted-foreground mb-2 pb-1.5 border-b border-border/60 flex items-center justify-between">
              <span>{floatingMenu.name} Models</span>
              <span className="text-[9px] text-[#CF2F2F] font-bold">{floatingMenu.models.length} Models</span>
            </div>

            {floatingMenu.models.length > 0 ? (
              <div className="space-y-1 max-h-60 overflow-y-auto pr-1">
                <button
                  type="button"
                  onClick={() => {
                    const slug = floatingMenu.slug
                    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
                    setFloatingMenu(null)
                    navigate(`/cars/brands/${slug}`)
                  }}
                  className="w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-bold text-[#CF2F2F] hover:bg-[#CF2F2F]/10 transition-colors flex items-center justify-between cursor-pointer"
                >
                  <span>All {floatingMenu.name} Vehicles</span>
                  <ChevronRight className="size-3" />
                </button>

                {floatingMenu.models.map((mod: any) => (
                  <button
                    key={mod.id || mod.slug}
                    type="button"
                    onClick={() => {
                      if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
                      setFloatingMenu(null)
                      navigate(`/cars/models/${mod.slug}`)
                    }}
                    className="w-full text-left px-2.5 py-1.5 rounded-lg text-xs font-semibold text-foreground/90 hover:text-[#CF2F2F] hover:bg-slate-100 dark:hover:bg-accent transition-colors flex items-center justify-between cursor-pointer"
                  >
                    <span>{mod.name}</span>
                    {mod.bodyType && (
                      <span className="text-[9px] text-muted-foreground uppercase font-bold">{mod.bodyType}</span>
                    )}
                  </button>
                ))}
              </div>
            ) : (
              <div className="p-2 text-center text-xs text-muted-foreground">
                <button
                  type="button"
                  onClick={() => {
                    const slug = floatingMenu.slug
                    if (closeTimerRef.current) clearTimeout(closeTimerRef.current)
                    setFloatingMenu(null)
                    navigate(`/cars/brands/${slug}`)
                  }}
                  className="text-[#CF2F2F] hover:underline font-bold text-xs cursor-pointer"
                >
                  Browse All {floatingMenu.name}
                </button>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* MOBILE MENU */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="space-y-4 border-t border-border bg-background px-4 py-4 sm:hidden"
          >
            <div className="flex flex-col space-y-2">
              {TOP_NAV_LINKS.map((link) => (
                <Link
                  key={link.label}
                  to={link.to}
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="py-1.5 text-sm font-semibold text-foreground"
                >
                  {link.label}
                </Link>
              ))}
            </div>

            <div className="flex items-center justify-between gap-3 border-t border-border pt-4">
              {isLoading ? (
                <span className="h-9 w-full animate-pulse rounded-lg bg-border/40" />
              ) : isAuthenticated ? (
                <Button size="sm" className="w-full" onClick={handleDashboardClick}>
                  <LayoutDashboard className="h-4 w-4" /> Go to Dashboard
                </Button>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1"
                    onClick={() => navigate(appRoutes.auth.login)}
                  >
                    <LogIn className="h-4 w-4" /> Sign In
                  </Button>
                  <Button
                    size="sm"
                    className="flex-1"
                    onClick={() => navigate(appRoutes.auth.register)}
                  >
                    <UserPlus className="h-4 w-4" /> Sign Up
                  </Button>
                </>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Global Public Cart Drawer */}
      <CartDrawer isOpen={isCartOpen} onClose={() => setIsCartOpen(false)} />
    </header>
  )
}